import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Phone, Check, Loader, AlertCircle } from "lucide-react";
import confetti from "canvas-confetti";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import { useAuth } from "../hooks/useAuth";
import insurancePolicyService from "../lib/InsurancePolicyService";

/**
 * Returns true if current time is Mon–Fri 8am–9pm Eastern.
 * Mirrors the helper in ViuChoiceScreen so the two screens stay consistent.
 */
const isWithinVATHours = () => {
  const now = new Date();
  const eastern = new Date(
    now.toLocaleString("en-US", { timeZone: "America/New_York" }),
  );
  const day = eastern.getDay(); // 0=Sun, 6=Sat
  const hour = eastern.getHours();
  return day >= 1 && day <= 5 && hour >= 8 && hour < 21;
};

const ViuCallScreen = ({ buyerData, onComplete, onBack }) => {
  const vatHours = isWithinVATHours();
  const { trackButtonClick } = useInteractionTracking();
  const { getIdToken } = useAuth();
  const headerRef = useRef(null);

  // "finalizing" | "ready" | "profile_incomplete" | "error"
  const [callStatus, setCallStatus] = useState("finalizing");
  const [errorMessage, setErrorMessage] = useState("");
  const [missingFields, setMissingFields] = useState("");

  const fireConfetti = () => {
    if (!headerRef.current) return;
    const rect = headerRef.current.getBoundingClientRect();
    const x = (rect.left + rect.width / 2) / window.innerWidth;
    const y = (rect.top + rect.height / 2) / window.innerHeight;
    confetti({
      particleCount: 100,
      spread: 80,
      origin: { x, y },
      colors: ["#7B61FF", "#D4F6C9", "#CEC5FF", "#FFD700", "#A1CE90"],
    });
  };

  const finalize = async () => {
    setCallStatus("finalizing");
    setErrorMessage("");
    setMissingFields("");

    try {
      const token = await getIdToken();
      if (!token) {
        setErrorMessage("Authentication required. Please try again.");
        setCallStatus("error");
        return;
      }

      console.log("Finalize Viu");
      const result = await insurancePolicyService.finalizeViuUrl(token);

      if (result.success) {
        setCallStatus("ready");
        fireConfetti();
      } else {
        const err = result.error || "";
        const lowerErr = err.toLowerCase();
        if (
          lowerErr.includes("missing") ||
          lowerErr.includes("required") ||
          lowerErr.includes("email") ||
          lowerErr.includes("phone") ||
          lowerErr.includes("name")
        ) {
          const colonIdx = err.indexOf(":");
          const fields =
            colonIdx !== -1 ? err.substring(colonIdx + 1).trim() : err;
          setMissingFields(fields);
          setCallStatus("profile_incomplete");
        } else {
          setErrorMessage(err || "Something went wrong. Please try again.");
          setCallStatus("error");
        }
      }
    } catch (error) {
      console.error("Error finalizing VIU handoff:", error);
      setErrorMessage("Something went wrong. Please try again.");
      setCallStatus("error");
    }
  };

  useEffect(() => {
    analyticsService.trackPageView("viu_call_screen", {
      buyer: buyerData?.name,
    });
    finalize();
  }, []);

  // Finalizing — loading state
  if (callStatus === "finalizing") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-[#F5F2FF] rounded-full flex items-center justify-center mx-auto">
            <Loader className="w-10 h-10 text-[#7B61FF] animate-spin" />
          </div>
          <h1 className="text-xl font-bold uppercase">
            Setting Up Your Advisor Call...
          </h1>
          <p className="text-darkGrey">
            We're preparing your handoff to{" "}
            <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span>.
          </p>
        </div>
      </div>
    );
  }

  // Profile incomplete — missing fields
  if (callStatus === "profile_incomplete") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-xl font-bold uppercase">Profile Update Needed</h1>
          <p className="text-darkGrey">
            Please update your profile with your{" "}
            <span className="font-semibold text-gray-900">{missingFields}</span>{" "}
            so an advisor can reach you.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              trackButtonClick("viu-call-retry-after-profile", {
                step_name: "viu_call_screen",
              });
              finalize();
            }}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
          >
            Try Again
          </button>

          {onBack && (
            <button
              onClick={() => {
                trackButtonClick("viu-call-back-to-choice", {
                  step_name: "viu_call_screen",
                });
                onBack();
              }}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (callStatus === "error") {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4" ref={headerRef}>
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-xl font-bold uppercase">Something Went Wrong</h1>
          <p className="text-darkGrey">{errorMessage}</p>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => {
              trackButtonClick("viu-call-retry", {
                step_name: "viu_call_screen",
              });
              finalize();
            }}
            className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors"
          >
            Try Again
          </button>

          {onBack && (
            <button
              onClick={() => {
                trackButtonClick("viu-call-back-to-choice", {
                  step_name: "viu_call_screen",
                });
                onBack();
              }}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  // Ready — finalize succeeded, agent will call back
  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="text-center space-y-4" ref={headerRef}>
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-10 h-10 text-green-500" />
        </div>
        <h1 className="text-xl font-bold uppercase">You're All Set!</h1>
        <p className="text-darkGrey">
          We've passed your information to{" "}
          <span style={{ color: "#7A5CFA" }}>{buyerData?.name}</span>.{" "}
          {vatHours
            ? "An advisor will reach out to you shortly to help with your personalized insurance quote."
            : "An advisor will reach out during business hours (Mon–Fri, 8am–9pm ET) to help with your personalized insurance quote."}
        </p>
      </div>

      {/* Buyer Logo */}
      {buyerData?.logo && (
        <div className="flex justify-center">
          <div className="bg-gray-50 p-4 rounded-lg">
            <img
              src={buyerData.logo}
              alt={buyerData.name}
              className="h-16 w-auto object-contain"
            />
          </div>
        </div>
      )}

      {/* Callback confirmation */}
      <Card className="border-2 border-[#7B61FF] bg-white rounded-[20px]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <Phone className="w-12 h-12 text-[#7B61FF] mx-auto" />
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {vatHours
                  ? "Expect a Call Within 10 minutes."
                  : "We'll Call You During Business Hours."}
              </h2>
              <p className="text-gray-600 text-sm">
                {vatHours
                  ? `A ${buyerData?.name} Advisor will call you to walk through your options and help you find the best coverage.`
                  : `A ${buyerData?.name} Advisor will call you Monday–Friday, 8am–9pm ET, to walk through your options and help you find the best coverage.`}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What to Expect */}
      <Card className="border-gray-200 rounded-[20px]">
        <CardContent className="p-5">
          <h3 className="font-bold text-gray-900 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>
                A {buyerData?.name} advisor will review your Ownli insurance
                profile
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              <span>They'll provide personalized quotes</span>
            </li>
            {/* {buyerData?.offer?.amount && (
              <li className="flex items-start gap-2">
                <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                <span>
                  Your reward will be approved after profile validation
                </span>
              </li>
            )} */}
          </ul>
        </CardContent>
      </Card>

      {/* <button
        onClick={() => {
          trackButtonClick("viu-call-continue", {
            step_name: "viu_call_screen",
          });
          onComplete();
        }}
        className="w-full flex items-center justify-center gap-2 uppercase font-bold border-2 border-[#7B61FF] text-[#7B61FF] px-4 py-3 rounded-[10px] hover:bg-[#F5F2FF] transition-colors"
      >
        Continue
      </button> */}
    </div>
  );
};

export default ViuCallScreen;
